import { createFileRoute, Link } from "@tanstack/react-router";
import { Moon, Leaf, Repeat, Flower2, Hand, Sparkles, Star, ArrowRight, Activity } from "lucide-react";
import approchePhoto from "@/assets/massage.webp.asset.json";
import heroPortrait from "@/assets/caroline-apropos.png.asset.json";
import auriculoAsset from "@/assets/auriculotherapie.webp.asset.json";
import massage1Asset from "@/assets/massage1.webp.asset.json";
import moxaAsset from "@/assets/moxa.webp.asset.json";
import heroVideo from "@/assets/hero-video.mp4.asset.json";
import partnerNutripure from "@/assets/partner-nutripure.png.asset.json";
import partnerFenioux from "@/assets/partner-fenioux.png.asset.json";
import partnerEspritArbres from "@/assets/esprit-arbres-logo.png.asset.json";
import partnerUnae from "@/assets/unae-logo.webp.asset.json";
import partnerSunday from "@/assets/partner-sunday-natural.jpg.asset.json";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Rénergescence — Santé naturelle & bien-être à Toulouse" },
      { name: "description", content: "Caroline Balech — accompagnement en santé naturelle à Toulouse. Naturopathie, massages, soins énergétiques pour retrouver équilibre, vitalité et sérénité." },
      { property: "og:title", content: "Rénergescence — Santé naturelle & bien-être" },
      { property: "og:description", content: "Approches naturelles et personnalisées à Toulouse pour retrouver bien-être, énergie et harmonie." },
      { property: "og:image", content: heroPortrait.url },
    ],
    links: [
      { rel: "canonical", href: "https://renergescence.com/" },
      { rel: "preload", as: "image", href: approchePhoto.url, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HealthAndBeautyBusiness",
          name: "Rénergescence",
          description: "Santé naturelle, massages et soins énergétiques",
          address: {
            "@type": "PostalAddress",
            streetAddress: "3 rue Ledru-Rollin",
            addressLocality: "Toulouse",
            postalCode: "31300",
            addressCountry: "FR",
          },
          telephone: "+33689676546",
          email: "contact@renergescence.com",
          priceRange: "50€–110€",
        }),
      },
    ],
  }),
  component: Index,
});

const problematiques = [
  { icon: Moon, title: "Troubles du sommeil", text: "Difficultés d'endormissement, réveils nocturnes, sommeil non réparateur…" },
  { icon: Leaf, title: "Fatigue physique et émotionnelle", text: "Manque d'énergie, stress, surcharge mentale, épuisement…" },
  { icon: Repeat, title: "Période de transition", text: "Deuil, séparation, changement professionnel ou personnel, préparation à un défi…" },
  { icon: Flower2, title: "Équilibre féminin", text: "Troubles menstruels, maternité, post-partum, ménopause…" },
  { icon: Activity, title: "Douleurs et maux corporels", text: "Sciatique, lombalgie, zona, arthrose, tendinites, contractures musculaires…" },
];

const prestations = [
  {
    href: "/prestations#naturopathie",
    img: auriculoAsset.url,
    icon: Leaf,
    title: "Accompagnements en santé naturelle",
    text: "Naturopathie · Médecine traditionnelle chinoise · Auriculothérapie — pour comprendre et agir sur l'origine des déséquilibres, retrouver énergie, vitalité et sérénité durablement.",
    price: "70€",
  },
  {
    href: "/prestations#massages",
    img: massage1Asset.url,
    icon: Hand,
    title: "Massages et techniques manuelles",
    text: "Des massages sur-mesure pour soulager les tensions, relâcher le corps et apaiser l'esprit.",
    price: "70€ – 110€",
  },
  {
    href: "/prestations#soins-energetiques",
    img: moxaAsset.url,
    icon: Sparkles,
    title: "Soins énergétiques",
    text: "Pour rééquilibrer vos énergies, libérer les blocages et favoriser un mieux-être global.",
    price: "50€ – 60€",
  },
];

const avis = [
  { quote: "Caroline est une praticienne très professionnelle, empathique et apaisante. On se sent en sécurité dans son cabinet et on en ressort serein. Merci à elle.", author: "Charlotte H." },
  { quote: "Un moment de détente absolue. Caroline est adorable et très professionnelle, le temps n'existe plus durant son massage.", author: "Céline S." },
  { quote: "Merci beaucoup Caroline ! Quel soulagement ! Ciao la douleur, les blocages et les tensions ! Bye bye sciatique, lombalgie et j'en passe…", author: "Valérie G." },
];

const partenaires = [
  { name: "Nutripure", logo: partnerNutripure.url, url: "https://www.nutripure.fr" },
  { name: "Laboratoires Fenioux", logo: partnerFenioux.url, url: "https://www.laboratoires-fenioux.com/fr" },
  { name: "L'Esprit des Arbres", logo: partnerEspritArbres.url, url: "https://www.lespritdesarbres.com/" },
  { name: "Unae", logo: partnerUnae.url, url: "https://www.unae.fr" },
  { name: "Sunday Natural", logo: partnerSunday.url, url: "https://www.sunday.fr/" },
];

function Index() {
  return (
    <>
      {/* HERO — Full-screen video */}
      <section
        className="relative -mt-[68px] flex items-center justify-center overflow-hidden md:-mt-[76px]"
        style={{ minHeight: "100svh" }}
      >
        <video
          src={heroVideo.url}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={approchePhoto.url}
          className="absolute inset-0 h-full w-full object-cover"
        >
          <track kind="captions" src="/captions/empty.vtt" srcLang="fr" label="Français" default />
        </video>
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(20,18,15,0.60) 0%, rgba(20,18,15,0.45) 40%, rgba(20,18,15,0.65) 100%)" }}
        />
        <div className="relative z-10 mx-auto max-w-[900px] px-5 pt-[68px] text-center md:pt-[76px]">
          <h1 className="sr-only">Rénergescence — Santé naturelle &amp; bien-être à Toulouse</h1>
          <p
            aria-hidden="true"
            className="font-display text-[34px] font-light leading-[1.08] text-white sm:text-[44px] md:text-[68px]"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.25)" }}
          >
            Votre parenthèse de <span className="italic">bien-être absolu</span>
          </p>
          <p
            className="mx-auto mt-5 max-w-[640px] text-[14px] leading-relaxed text-white/90 sm:text-[15px] md:text-[17px]"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.3)" }}
          >
            Naturopathie, massages et soins énergétiques dans une approche globale du mieux-être.
          </p>
          <div className="mt-7 flex justify-center">
            <Link
              to="/contact"
              className="inline-flex min-h-[52px] items-center gap-2 rounded-full px-8 py-3.5 text-[13px] font-medium uppercase tracking-[0.16em] text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "#7A8A76" }}
            >
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </section>

      {/* PROBLÉMATIQUES */}
      <section className="py-8 md:py-14" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[1200px] px-6">
          <h2 className="text-center text-[11px] font-medium uppercase tracking-[0.32em]" style={{ color: "#6a6258" }}>
            Vous traversez actuellement
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4 md:mt-12 md:gap-5">
            {problematiques.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="w-full rounded-[12px] border bg-white p-7 md:w-[calc(50%-10px)] lg:w-[calc(25%-15px)]"
                style={{ borderColor: "#e8e0d8", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: "#F0EBE4", color: "#7A8A76" }}>
                  <Icon size={22} strokeWidth={1.4} />
                </div>
                <h3 className="mt-5 font-display text-xl font-normal" style={{ color: "#2D2B28" }}>{title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#6a6258" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROCHE */}
      <section className="relative overflow-hidden py-8 md:py-14" style={{ backgroundColor: "#F8F5F0" }}>
        <Leaf
          aria-hidden
          size={420}
          strokeWidth={0.6}
          className="pointer-events-none absolute -right-20 top-1/2 -translate-y-1/2"
          style={{ color: "#7A8A76", opacity: 0.06 }}
        />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-8 px-6 md:grid-cols-2 md:gap-16">
          <div className="overflow-hidden rounded-[12px] md:aspect-[4/5]">
            <img src={approchePhoto.url} alt="Cabinet de Caroline Balech" width={800} height={1000} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: "var(--color-sage-text)" }}>Mon approche</p>
            <h2 className="mt-4 font-display text-[36px] font-light leading-[1.1]" style={{ color: "#2D2B28" }}>
              Une approche globale<br />et <span className="italic">personnalisée</span>
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: "#4A4742" }}>
              Chaque personne est unique, avec son histoire, ses besoins et ses objectifs. Mon rôle est de vous accompagner avec bienveillance vers un équilibre durable.
            </p>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#4A4742" }}>
              J'utilise différentes approches naturelles et techniques manuelles pour comprendre l'origine des déséquilibres et vous proposer des solutions adaptées à votre situation.
            </p>
            <Link
              to="/a-propos"
              className="mt-8 inline-flex items-center gap-2 rounded-[6px] border px-6 py-3 text-[13px] font-medium transition-colors hover:bg-white"
              style={{ borderColor: "#7A8A76", color: "var(--color-sage-text)" }}
            >
              En savoir plus sur moi <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PRESTATIONS */}
      <section className="py-8 md:py-14" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[1200px] px-6">
          <p className="text-center text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: "var(--color-sage-text)" }}>
            Mes accompagnements
          </p>
          <h2 className="mt-3 text-center font-display text-[28px] font-light leading-[1.1] md:text-[36px]" style={{ color: "#2D2B28" }}>
            Des solutions naturelles<br />pour votre <span className="italic">bien-être</span>
          </h2>
          <div className="mt-8 grid gap-6 md:mt-14 md:grid-cols-3 md:gap-7">
            {prestations.map(({ img, icon: Icon, title, text, price }) => (
              <article
                key={title}
                className="overflow-hidden rounded-[12px] border bg-white"
                style={{ borderColor: "#e8e0d8", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={img} alt={title} width={600} height={400} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  <div
                    className="absolute left-5 top-5 flex h-11 w-11 items-center justify-center rounded-full backdrop-blur-sm"
                    style={{ backgroundColor: "rgba(255,255,255,0.85)", color: "#7A8A76" }}
                  >
                    <Icon size={20} strokeWidth={1.4} />
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-display text-2xl font-normal" style={{ color: "#2D2B28" }}>{title}</h3>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: "#6a6258" }}>{text}</p>
                  <div className="mt-6 flex items-center justify-between border-t pt-5" style={{ borderColor: "#e8e0d8" }}>
                    <span className="text-sm font-medium" style={{ color: "var(--color-gold-text)" }}>{price}</span>
                  <a
                    href={prestations.find((p) => p.title === title)?.href ?? "/prestations"}
                    className="text-[13px] font-medium"
                    style={{ color: "var(--color-sage-text)" }}
                  >
                    En savoir plus →
                  </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AVIS */}
      <section className="py-8 md:py-14" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[1200px] px-6">
          <p className="text-center text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: "var(--color-sage-text)" }}>
            Ils en parlent le mieux
          </p>
          <h2 className="mt-3 text-center font-display text-[28px] font-light leading-[1.1] md:text-[36px]" style={{ color: "#2D2B28" }}>
            Ce qu'ils <span className="italic">en disent</span>
          </h2>
          <div className="mt-5 flex items-center justify-center gap-1" style={{ color: "#B59C7A" }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} size={18} fill="currentColor" strokeWidth={0} />
            ))}
          </div>
          <div className="mt-8 grid gap-5 md:mt-14 md:grid-cols-3 md:gap-6">
            {avis.map((a) => (
              <figure
                key={a.author}
                className="relative rounded-[12px] border p-7"
                style={{ backgroundColor: "#f8f4f0", borderColor: "#e8e0d8" }}
              >
                <span
                  aria-hidden
                  className="absolute -top-2 left-5 font-display text-[80px] leading-none"
                  style={{ color: "#B59C7A", opacity: 0.4 }}
                >
                  &ldquo;
                </span>
                <blockquote className="relative mt-6 font-display text-[17px] italic leading-relaxed" style={{ color: "#2D2B28" }}>
                  {a.quote}
                </blockquote>
                <figcaption className="mt-6 text-sm" style={{ color: "#6a6258" }}>
                  <p className="font-medium" style={{ color: "#2D2B28" }}>{a.author}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="flex gap-0.5" style={{ color: "#B59C7A" }}>
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={12} fill="currentColor" strokeWidth={0} />
                      ))}
                    </span>
                    <span className="text-xs">Avis Google vérifié</span>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href="https://share.google/n6201Hhd0MmW72ymp"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[13px] font-medium"
              style={{ color: "var(--color-sage-text)" }}
            >
              <span className="border-b border-current pb-0.5">Voir tous les avis sur Google</span> →
            </a>
          </div>
        </div>
      </section>

      {/* PARTENAIRES */}
      <section className="py-8 md:py-14" style={{ backgroundColor: "#F8F5F0" }}>
        <div className="mx-auto max-w-[1200px] px-6 text-center">
          <p className="text-[11px] font-medium uppercase tracking-[0.28em]" style={{ color: "var(--color-sage-text)" }}>
            Partenaires de confiance
          </p>
          <h2 className="mt-3 font-display text-[28px] font-light leading-[1.1] md:text-[36px]" style={{ color: "#2D2B28" }}>
            Des marques <span className="italic">engagées</span>
          </h2>
          <div className="mt-8 flex snap-x snap-mandatory items-center gap-x-8 overflow-x-auto px-2 pb-2 md:mt-14 md:flex-wrap md:justify-center md:gap-x-16 md:gap-y-10 md:overflow-visible md:px-0">
            {partenaires.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={p.name}
                className="flex h-14 w-28 shrink-0 snap-center items-center justify-center md:h-20 md:w-36"
              >
                <img
                  src={p.logo}
                  alt={`Logo ${p.name}`}
                  width={144}
                  height={80}
                  loading="lazy"
                  decoding="async"
                  className="max-h-full max-w-full object-contain transition-opacity hover:opacity-100"
                  style={{ mixBlendMode: "multiply", filter: "grayscale(100%) contrast(0.9)", opacity: 0.65 }}
                />
              </a>
            ))}
          </div>
          <Link
            to="/blog"
            className="mt-10 inline-flex items-center gap-2 rounded-[6px] border px-7 py-3 text-[13px] font-medium transition-colors hover:bg-white md:mt-14"
            style={{ borderColor: "#7A8A76", color: "var(--color-sage-text)" }}
          >
            Découvrir tous mes partenaires <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </>
  );
}